import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { assetUrlsQueryOptions } from '@/entities/asset';
import { useAssetVariant } from '@/entities/asset/model/useAssetVariant';
import {
  awaitsIngestion,
  awaitsPoster,
  brandMediaQueryOptions,
  mediaUrlIds,
  toMediaTile,
  type BrandMediaItem,
  type MediaTile,
} from '@/entities/brand-media';
import type { ListBrandMediaQuery } from '@/shared/lib/brand-media';

/**
 * How often to ask again while a clip is waiting for its still.
 *
 * Extraction takes a second or two for a short clip, so this is about how long someone stares at a
 * tile before it stops looking broken, not about load.
 */
const POSTER_POLL_MS = 2000;

/**
 * How often to ask again while a row is waiting to be catalogued.
 *
 * Slower than the poster poll, because the wait is longer: a description is a model call behind a
 * concurrency cap, so this is tens of seconds for one file and minutes for an imported pack. Asking
 * every two seconds for that long would be a lot of requests to learn nothing.
 */
const INGESTION_POLL_MS = 5000;

export interface MediaLibrary {
  /** Sized for a picker grid tile, so this is what `MediaGrid` draws. */
  tiles: MediaTile[];
  /**
   * The opened row's own tile, resolved at the size the detail panel and the enlarged preview
   * judge artwork at -- the same size the canvas does, so neither can disagree with the grid
   * about how something looks. Undefined whenever `openedId` is undefined or matches no row.
   */
  openedTile: MediaTile | undefined;
  /** The rows the tiles were flattened from, for a caller that needs more than a thumbnail does. */
  rows: BrandMediaItem[];
  isLoading: boolean;
  /**
   * Whether the rows in hand may already be behind the server.
   *
   * True while a refetch is in flight over rows that are already on screen, which `isLoading` is
   * not: that one is only the first fetch. A caller that writes a row back whole -- rebuilding a
   * tag list from what it holds -- needs to know the difference, because doing so during this
   * window would undo whatever prompted the refetch.
   */
  isStale: boolean;
  isError: boolean;
  /** Asks again after a failure. Stable, so a caller may hang an effect on it. */
  retry: () => void;
}

/**
 * The library, asked exactly what the caller asks.
 *
 * Deliberately takes a whole query rather than composing one. `useFrameMedia` builds its question
 * from a frame, because only the canvas knows what a frame can take; this view has no frame and no
 * business inventing constraints, so whatever narrows it comes from whoever mounted it. That is what
 * lets the same view sit under a workspace, a project or a brief without being rebuilt.
 *
 * `openedId` names the one row, if any, that a detail panel or preview dialog is currently reading:
 * that surface asks for a bigger derivative than a grid tile does, and asking for it on every row
 * merely to have it ready for the one somebody might open would resolve a size nothing on screen
 * uses.
 */
export function useMediaLibrary(query: ListBrandMediaQuery, openedId?: string): MediaLibrary {
  const media = useQuery({
    ...brandMediaQueryOptions(query),
    // A still, a description and the tags are all written behind the upload, so the row that comes
    // back from adding one carries none of them and nothing else would ever ask again. Read off this
    // query's own data rather than from `rows` below, which would be circular, and false as soon as
    // every row is finished -- so this is a burst after an upload, not a standing poll.
    refetchInterval: (observed) => {
      const rows = observed.state.data ?? [];
      const now = Date.now();
      if (awaitsPoster(rows, now)) return POSTER_POLL_MS;
      return awaitsIngestion(rows, now) && INGESTION_POLL_MS;
    },
  });

  const rows = media.data ?? [];
  const thumbVariant = useAssetVariant('thumb');
  const urls = useQuery(assetUrlsQueryOptions(mediaUrlIds(rows), thumbVariant));

  const previewVariant = useAssetVariant('preview');
  const openedRow = rows.find((row) => row.id === openedId);
  const previewIds = openedRow === undefined ? [] : mediaUrlIds([openedRow]);
  const previewUrls = useQuery(assetUrlsQueryOptions(previewIds, previewVariant));

  // Only the listing: the urls follow from the rows it returns, so asking for them again before
  // there are any rows to ask about would be a request for nothing.
  const { refetch } = media;
  const retry = useCallback(() => void refetch(), [refetch]);

  return {
    // Stamped here rather than inside the tile, so what a tile says about ingestion is a function of
    // its inputs and a test can put a row at any age it likes.
    tiles: rows.map((row) => toMediaTile(row, urls.data, Date.now())),
    // Falls back to the grid batch's own url while the preview batch is still in flight or has
    // failed, so the panel opens on what the grid already showed rather than on nothing, then
    // upgrades to the preview variant once that batch lands.
    openedTile:
      openedRow === undefined
        ? undefined
        : toMediaTile(openedRow, { ...urls.data, ...previewUrls.data }, Date.now()),
    rows,
    isLoading: media.isLoading,
    isStale: media.isFetching && !media.isLoading,
    isError: media.isError,
    retry,
  };
}
